import {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import type { User, JwtPayload } from '@shared/index';
import { authApi, tokenStore } from '@/lib/api';

// ─────────────────────────────────────────────────────────────────────────────
// JWT utilities (no library — just base64 decode)
// ─────────────────────────────────────────────────────────────────────────────

function decodeJwt(token: string): JwtPayload | null {
  try {
    const [, payloadB64] = token.split('.');
    // Replace URL-safe chars and add padding
    const padded = payloadB64.replace(/-/g, '+').replace(/_/g, '/');
    const json   = atob(padded);
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

/** Build a minimal User from a decoded JWT payload. */
function userFromJwt(payload: JwtPayload): User {
  return {
    id:          payload.id,
    email:       payload.email,
    username:    payload.username,
    role:        payload.role,
    avatarUrl:   null,
    bio:         null,
    isActive:    true,
    lastLoginAt: null,
    createdAt:   new Date(0).toISOString(),
    updatedAt:   new Date(0).toISOString(),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// BroadcastChannel — cross-tab auth sync
// ─────────────────────────────────────────────────────────────────────────────

type BcMessage = { type: 'logout' } | { type: 'login' };

const BC_CHANNEL = 'ph_auth';

// ─────────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────────

export interface AuthContextValue {
  user:            User | null;
  accessToken:     string | null;
  isLoading:       boolean;
  isAuthenticated: boolean;
  login:           (email: string, password: string) => Promise<void>;
  register:        (username: string, email: string, password: string, inviteCode?: string) => Promise<void>;
  logout:          () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user,      setUser]      = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const refreshTimerRef           = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate                  = useNavigate();
  const bcRef                     = useRef<BroadcastChannel | null>(null);

  // ── Auto-refresh scheduler ─────────────────────────────────────────────────

  const scheduleRefresh = useCallback((token: string) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);

    const payload = decodeJwt(token);
    if (!payload?.exp) return;

    const msUntilExpiry = payload.exp * 1_000 - Date.now();
    const refreshIn     = Math.max(0, msUntilExpiry - 60_000); // 60s before expiry

    refreshTimerRef.current = setTimeout(async () => {
      try {
        const { accessToken: newToken } = await authApi.refresh();
        tokenStore.set(newToken);
        const decoded = decodeJwt(newToken);
        if (decoded) setUser((prev) => prev ? { ...prev, ...userFromJwt(decoded) } : userFromJwt(decoded));
        scheduleRefresh(newToken);
      } catch {
        // Refresh failed — clear auth state
        tokenStore.clear();
        setUser(null);
        navigate('/login', { replace: true });
      }
    }, refreshIn);
  }, [navigate]);

  // ── Store access token + schedule refresh ──────────────────────────────────

  const setAuth = useCallback((token: string, fullUser?: User) => {
    tokenStore.set(token);
    const payload = decodeJwt(token);
    if (fullUser) {
      setUser(fullUser);
    } else if (payload) {
      setUser(userFromJwt(payload));
    }
    scheduleRefresh(token);
  }, [scheduleRefresh]);

  // ── Clear auth state ───────────────────────────────────────────────────────

  const clearAuth = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    tokenStore.clear();
    setUser(null);
  }, []);

  // ── Silent refresh on mount (uses httpOnly cookie) ─────────────────────────

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { accessToken } = await authApi.refresh();
        if (!cancelled) setAuth(accessToken);
      } catch {
        if (!cancelled) tokenStore.clear();
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── BroadcastChannel — sync logout across tabs ─────────────────────────────

  useEffect(() => {
    if (!('BroadcastChannel' in window)) return;

    const bc = new BroadcastChannel(BC_CHANNEL);
    bcRef.current = bc;

    bc.addEventListener('message', (e: MessageEvent<BcMessage>) => {
      if (e.data.type === 'logout') {
        clearAuth();
        navigate('/login', { replace: true });
      }
    });

    return () => { bc.close(); bcRef.current = null; };
  }, [clearAuth, navigate]);

  // ── Auth actions ───────────────────────────────────────────────────────────

  const login = useCallback(async (email: string, password: string): Promise<void> => {
    const { user: fullUser, accessToken } = await authApi.login(email, password);
    setAuth(accessToken, fullUser);
    bcRef.current?.postMessage({ type: 'login' } satisfies BcMessage);
  }, [setAuth]);

  const register = useCallback(async (
    username: string,
    email: string,
    password: string,
    inviteCode?: string,
  ): Promise<void> => {
    const { user: fullUser, accessToken } = await authApi.register(username, email, password, inviteCode);
    setAuth(accessToken, fullUser);
    bcRef.current?.postMessage({ type: 'login' } satisfies BcMessage);
  }, [setAuth]);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await authApi.logout();
    } catch {
      // Logout best-effort — clear state regardless
    } finally {
      clearAuth();
      bcRef.current?.postMessage({ type: 'logout' } satisfies BcMessage);
      navigate('/', { replace: true });
    }
  }, [clearAuth, navigate]);

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken:     tokenStore.get(),
        isLoading,
        isAuthenticated: !!user,
        login,
        register,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// Re-export useAuth so pages can import from either location
export { useAuth } from '@/hooks/useAuth';
